/**
 * The purchase sheet's calls, in the server's own description of the
 * purchase (LIS-1173, the purchase design's §2.1): the buyer's signed-in
 * read of the purchase, the answered start, the attempt view on every read
 * of the attempt, the one answer to whatever its screen asks, the store's
 * delivery asked about one destination, and the seller account opened from
 * the buyer's names. Nothing here names a seller.
 */
import { z } from "zod";
import {
  attemptViewSchema,
  purchaseSchema,
  startRefusalSchema,
  type Answers,
  type AttemptAnswer,
  type AttemptView,
  type Destination,
  type Purchase,
  type StartRefusal,
} from "@libs/platform-wire/purchase";
import type { BuyerIdentity, CardSummary, CardTransport } from "@libs/buyer-identity-client";
import type {
  AnsweredStart,
  ChosenDelivery,
  DeliveryEstimate,
  PurchaseCapability,
  PurchaseOpened,
  PurchaseSignal,
  PurchaseSignalled,
} from "../contracts";
import { JourneyError } from "../contracts";
import { failureDelay, notFoundPollStatus, terminalPollStatus, NOT_FOUND_GRACE_MS, POLL_INTERVAL_MS } from "../progress-watch";

/** An attempt as the sheet reads it: its id and the view the server
 *  compiled for it, null only where the server could not compile one (it
 *  reports that itself). The rest of the attempt's read is not the sheet's
 *  to know. */
export const attemptReadSchema = z.object({ id: z.uuid(), view: attemptViewSchema.nullable() });
export type AttemptRead = { id: string; view: AttemptView | null };

/** A signed-in read: the purchase for this buyer, with their saved answers
 *  in its fields; null where the access offers no listing. */
export const signedInPurchaseSchema = purchaseSchema.nullable();

/** A start as the sheet makes it. The host's transport adds where it comes
 *  from (`origin`), its funnel and its reporting fields. */
export type PurchaseStart = Omit<AnsweredStart, "origin" | "funnelId" | "adContext" | "saleSource">;

/** The store's delivery for a basket of the purchase's listing, asked about
 *  one destination (§2.7). */
export type DestinationEstimateInput = {
  accessId: string;
  sheetId: string;
  listingId: string;
  lines: { item_id: string; quantity: number }[];
  destination: Destination;
};

/** The tickets sheet opened on a listing (`sheet.opened`, spine v4 §1):
 *  `visitorId` is the visitor's own anonymous id, signed in or not. */
export type PurchaseOpenInput = {
  visitorId: string;
  accessId: string;
  listingId: string;
  /** When the event page the sheet is on opened (ISO). */
  pageOpenedAt?: string;
};

/** A start the server refused, with the words it built for the buyer. */
export class StartRefused extends JourneyError {
  constructor(
    code: string,
    public refusal: StartRefusal,
  ) {
    super(code);
  }
}

export type PurchaseTransport = CardTransport & {
  /** The signed-in buyer's own read of the access's purchase (never
   *  cached), for `deliverTo` once their address names where it goes. */
  read(accessId: string, at?: { deliverTo?: string }): Promise<Purchase | null>;
  /** The buyer's email and saved cards. */
  identity(): Promise<BuyerIdentity>;
  estimateDelivery(input: DestinationEstimateInput): Promise<DeliveryEstimate>;
  deliveryEstimate(sheetId: string, id: string): Promise<DeliveryEstimate>;
  /** The buyer's first and last name are answered: their seller account is
   *  opened from them now, ahead of Pay. Saves nothing; best effort. */
  prepare(input: { accessId: string; listingId: string; answers: Answers }): Promise<void>;
  /** The sheet opened: the purchase it opens and warms (`spine`), or
   *  `runner`, where Buy starts the purchase as always. */
  open(input: PurchaseOpenInput): Promise<PurchaseOpened>;
  /** One of the sheet's signals, with its capability. `keepalive`: sent so
   *  it outlives the page (the sheet closing as the page goes). */
  signal(
    capability: PurchaseCapability,
    signal: PurchaseSignal,
    options?: { keepalive?: boolean },
  ): Promise<PurchaseSignalled>;
  start(input: PurchaseStart): Promise<AttemptRead>;
  active(accessId: string): Promise<AttemptRead | null>;
  status(id: string): Promise<AttemptRead>;
  cancel(id: string): Promise<AttemptRead>;
  answer(id: string, answer: AttemptAnswer): Promise<AttemptRead>;
  watch(id: string, onStatus: (attempt: AttemptRead) => void, onError: (terminal: boolean) => void): () => void;
};

/** An attempt still under way: a view not yet ended (or none read). */
export const attemptOpen = (attempt: AttemptRead | null): boolean =>
  !!attempt && (attempt.view === null || attempt.view.lifecycle !== "ended");

/**
 * What says, as it happens, that an attempt may have changed: the API's
 * realtime socket. Each change is told with the attempt it names, or null
 * when it names none (a socket that has just connected, which missed what
 * was published while it was away). Returns the function that stops it.
 */
export type AttemptChanges = (onChange: (attemptId: string | null) => void) => () => void;

/** The realtime scope that names a purchase attempt (`params.attemptId`). */
export const ATTEMPT_REALTIME_SCOPE = "buyOnBehalf.attempt";

/** The attempts a realtime message says changed: each `buyOnBehalf.attempt`
 *  command's attempt, or null for one naming none (the connect resync). */
export const changedAttempts = (commands: readonly { scope: string; params?: Record<string, string> }[]) =>
  commands.filter((command) => command.scope === ATTEMPT_REALTIME_SCOPE).map((command) => command.params?.attemptId ?? null);

/**
 * The attempt's reads until it ends: one at once whenever `changes` says it
 * changed, and one every `POLL_INTERVAL_MS` after the last as the fallback
 * (a lost message, a socket that cannot connect, a host with none). One read
 * is in flight at a time; a change heard during it reads again after it, so
 * no read is answered out of order. Each read that changes the view is told,
 * unless it is older than the one told last (a lower sequence). Backs off
 * after failures; a read the server refuses for good (signed out, not
 * theirs) is told as terminal at once. The attempt it watches is one the
 * server said exists, so its not found is read again with the backoff, and
 * is terminal only once its reads have answered nothing else for
 * `NOT_FOUND_GRACE_MS`.
 */
export function watchAttempt(options: {
  id: string;
  poll(): Promise<AttemptRead>;
  onStatus(value: AttemptRead): void;
  onError(terminal: boolean): void;
  changes?: AttemptChanges;
  intervalMs?: number;
}): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let failures = 0;
  /** When this run of reads answering not found began. */
  let notFoundSince: number | null = null;
  let last: string | null = null;
  let lastSequence = 0;
  let reading = false;
  let again = false;
  const listening: { unlisten?: () => void } = {};
  const stop = () => {
    stopped = true;
    clearTimeout(timer);
    listening.unlisten?.();
  };
  const schedule = (delay: number) => {
    clearTimeout(timer);
    if (!stopped) timer = setTimeout(() => void read().catch(stop), delay);
  };
  const read = async () => {
    if (stopped) return;
    if (reading) {
      again = true;
      return;
    }
    reading = true;
    clearTimeout(timer);
    let next: number;
    try {
      const value = await options.poll();
      if (stopped) return;
      failures = 0;
      notFoundSince = null;
      const key = JSON.stringify(value.view);
      const older = !!value.view && value.view.sequence < lastSequence;
      if (key !== last && !older) {
        last = key;
        lastSequence = value.view?.sequence ?? lastSequence;
        options.onStatus(value);
      }
      if (!attemptOpen(value)) {
        stop();
        return;
      }
      next = options.intervalMs ?? POLL_INTERVAL_MS;
    } catch (error) {
      if (stopped) return;
      const status = (error as { status?: unknown } | null)?.status;
      const notFound = notFoundPollStatus(status);
      notFoundSince = notFound ? (notFoundSince ?? Date.now()) : null;
      const gone = notFoundSince !== null && Date.now() - notFoundSince >= NOT_FOUND_GRACE_MS;
      if (terminalPollStatus(status) && (!notFound || gone)) {
        options.onError(true);
        stop();
        return;
      }
      options.onError(false);
      next = failureDelay(++failures);
    } finally {
      reading = false;
    }
    if (again && failures === 0) {
      again = false;
      void read().catch(stop);
      return;
    }
    again = false;
    schedule(next);
  };
  listening.unlisten = options.changes?.((attemptId) => {
    if (attemptId === null || attemptId === options.id) void read().catch(stop);
  });
  void read().catch(stop);
  return stop;
}

/** An attempt read, held to its schema: a view outside it fails the read. */
export const attemptReadOf = (value: unknown): AttemptRead => attemptReadSchema.parse(value);

/** A start refusal's words, where the error body carries them. */
export const startRefusalOf = (value: unknown): StartRefusal | null => {
  const parsed = startRefusalSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export type { Answers, AttemptAnswer, AttemptView, CardSummary, ChosenDelivery, Destination, Purchase, StartRefusal };
